import type {
  EditorBlock,
  ImageChoiceOption,
  LoaderQuestion,
} from '@/store/editorSlice';
import { richFieldToPlainText, type RichField } from '@/types/rich-text';
import { blockHonorsOptionNav } from '@/logic-engine/funnel-graph';
// Задание stylepack/unit-set: набор единиц пикера — одна дверь.
import { pickerUnits } from '@/components/editor/unitPickerLook';
import {
  SENSITIVE_CONSENT_BLOCK_TYPE,
  readSensitiveConsent,
  sensitiveShareEnabled,
  sensitiveShareValueKey,
  type SensitiveConsentState,
} from './sensitiveConsent';

/**
 * WEB-1140 (П2): человекочитаемый текст вопроса для SCREEN_ANSWER-аналитики —
 * ровно то, что пользователь видел на экране. Для обычных вопросных блоков
 * это block.content (PublicBlockRenderer рендерит его через richText у всех
 * ANSWER_BLOCK_TYPES), для модалок лоадера — question. content бывает и
 * строкой, и массивом rich-спанов (шрам: тип говорит string, рантайм — нет),
 * поэтому плоский текст добываем через richFieldToPlainText. Пусто → undefined:
 * label не отправляем, бэкенд падает обратно на blockId.
 */
function questionLabel(text: unknown): string | undefined {
  if (typeof text !== 'string' && !Array.isArray(text)) return undefined;
  const plain = richFieldToPlainText(text as RichField).trim();
  return plain === '' ? undefined : plain.slice(0, 255);
}

/**
 * WEB-925 (гэп 1): ключ, под которым ответ из модалки лоадера ложится в
 * FormValues. Собственный namespace нужен потому, что `values[block.id]` у
 * лоадера уже занят маркером завершения 'done' — запись ответа туда продвинула
 * бы экран раньше времени (blockAutoAdvances + !!values[b.id]).
 */
const LOADER_QUESTION_KEY_MARK = '::loaderQ';

export function loaderQuestionValueKey(blockId: string, index: number): string {
  return `${blockId}${LOADER_QUESTION_KEY_MARK}${index}`;
}

/**
 * WEB-1011: узнать служебный ключ модалки среди прочих значений формы. Разбор
 * ключа живёт рядом со сборкой намеренно — разъехавшийся разделитель как раз и
 * даёт молчаливую утечку служебной записи туда, где ждут ответ пользователя.
 */
export function isLoaderQuestionValueKey(key: string): boolean {
  return key.includes(LOADER_QUESTION_KEY_MARK);
}

/**
 * WEB-1301: companion-ключ единицы измерения для weight_picker/height_picker.
 *
 * values[block.id] у этих блоков — ГОЛОЕ число ("70"), без единицы: единица —
 * локальный UI-тумблер kg/lb (или cm/ft) внутри UnitPickerBlock, который сам
 * по себе в форму не пишется. Единицу НЕЛЬЗЯ склеивать прямо в
 * values[block.id] (например "70 kg") — движок условий (logic-engine/
 * operators.ts, gt/gte/lt/lte) сравнивает значение через Number(lhsStr);
 * "70 kg" даёт NaN и тихо ломает любую ветвление-логику по весу/росту.
 * Поэтому единица живёт под ОТДЕЛЬНЫМ ключом, тем же приёмом, что и ответы
 * модалок лоадера (loaderQuestionValueKey выше) — и по той же причине должна
 * фильтроваться, если что-то перебирает values/answers как есть (сторож —
 * isPickerUnitValueKey).
 */
const PICKER_UNIT_KEY_MARK = '::unit';

export function pickerUnitValueKey(blockId: string): string {
  return `${blockId}${PICKER_UNIT_KEY_MARK}`;
}

export function isPickerUnitValueKey(key: string): boolean {
  // Ревизия десятки 7: endsWith, не includes — блок с id, случайно содержащим
  // '::unit' в середине, не должен вырезаться из сводки.
  return key.endsWith(PICKER_UNIT_KEY_MARK);
}

/**
 * Единица измерения по умолчанию для weight_picker/height_picker.
 * Ревизия десятки 7: ТРИ слоя, как в рендерере (PublicBlockRendererExtended,
 * case weight_picker/height_picker): явное поле блока → плоское `unitDefault`
 * AI-сгенерированного блока → системный дефолт. Раньше средний слой здесь
 * отсутствовал — посетитель AI-блока видел «lb», а в аналитику при
 * непереключённом тумблере уезжало «kg». Экспорт — чтобы QuizScreenWizard
 * (blockMeta.unitDefault для сводки) читал ту же формулу, а не свою копию.
 */
export function resolvePickerUnitDefault(block: EditorBlock): string {
  // Задание stylepack/unit-set: одна единица в наборе — ответ в ней,
  // противоречащее unitDefault не действует (дверь — pickerUnits).
  const units = pickerUnits(block.type, block.unitOptions);
  if (units.length === 1) return units[0];
  const aiUnit = (block as { unitDefault?: string }).unitDefault;
  if (block.type === 'weight_picker')
    return block.weightPickerUnitDefault ?? aiUnit ?? 'kg';
  return block.heightPickerUnitDefault ?? aiUnit ?? 'cm';
}

function defaultPickerUnit(block: EditorBlock): string {
  return resolvePickerUnitDefault(block);
}

/** Тип, под которым ответы модалок уходят в SCREEN_ANSWER-аналитику. */
export const LOADER_QUESTION_BLOCK_TYPE = 'loader_question';

/**
 * WEB-2162 п.3: тип ВТОРОЙ, независимой записи согласия — на передачу третьим
 * лицам. Отдельный тип, а не поле внутри первой записи: закон штата Вашингтон
 * требует раздельные согласия, и склеенную запись потом не расцепить без
 * миграции данных (причина, записанная в WEB-2160).
 */
export const SENSITIVE_CONSENT_SHARE_BLOCK_TYPE = 'sensitive_consent_share';

/**
 * Вопросы лоадера читаются под ОБОИМИ именами: редактор пишет loaderQuestions,
 * генерация — короткое questions (класс field-desync WEB-780/605).
 */
export function loaderQuestionList(block: EditorBlock): LoaderQuestion[] {
  const ai = (block as unknown as { questions?: LoaderQuestion[] }).questions;
  const list = block.loaderQuestions ?? ai;
  return Array.isArray(list) ? list : [];
}

/**
 * WEB-682 (ретест 27.07): тоггл автоперехода лоадера сохраняется редактором
 * под именем loaderAutoAdvance, а AI-генерация пишет короткое autoAdvance —
 * плеер обязан читать ОБА (класс field-desync WEB-780/605). Для лоадера
 * дефолт true (фабрика и генерация включают его по умолчанию).
 *
 * WEB-848: extracted from PublicQuiz.tsx (behavior-preserving move).
 */
export function blockAutoAdvances(b: EditorBlock): boolean {
  if (b.type === 'single_choice') return b.autoAdvance === true;
  // WEB-926 (5-6): обе карточки-реакции живут сериями экранов — как только
  // реакция выбрана, экрану больше нечего показывать. Собственного поля не
  // заводим намеренно (шрам field-desync WEB-780/605): тумблер, фабрика и
  // генерация пишут ОДНО имя autoAdvance. Дефолта здесь нет — блок без поля
  // не прыгает, значение ставит фабрика редактора.
  if (b.type === 'statement_agreement' || b.type === 'preference_card') {
    return b.autoAdvance === true;
  }
  if (b.type === 'loader_processing') {
    // Дефолта здесь нет намеренно: фабрика редактора и генерация ставят
    // значение явно; legacy-блок без поля ведёт себя по-старому (не прыгает).
    return (b.loaderAutoAdvance ?? b.autoAdvance) === true;
  }
  // Выбор картинкой (stylepack/image-choice-auto, 21.10.2026): то же ОДНО имя
  // autoAdvance, что у соседей. Множественный выбор не уходит никогда —
  // человек ещё отмечает картинки, «выбор сделан» плееру не виден.
  if (b.type === 'image_choice') {
    return b.autoAdvance === true && !imageChoiceIsMultiple(b);
  }
  return false;
}

/**
 * Множественный ли выбор картинкой. У поля два имени (реестр пар:
 * `multiple` генерации ↔ `imageChoiceMultiple` кабинета); имя кабинета
 * приоритетнее — ровно как читает отрисовка (`ImageChoiceRenderer.tsx`).
 */
export function imageChoiceIsMultiple(b: EditorBlock): boolean {
  const ai = (b as unknown as { multiple?: unknown }).multiple;
  return (b.imageChoiceMultiple ?? ai) === true;
}

/**
 * Выбранный вариант выбора картинкой для яруса 2 переходов (ветвление по
 * `nextScreenId` варианта) — у ОДИНОЧНОГО выбора, с автопереходом и с
 * кнопкой «Далее» одинаково, как у single_choice (решение владельца
 * 22.10.2026 ~22:40, D6 расширено на image_choice; прежде — только с
 * автопереходом). Одиночный ли — решает общий признак
 * `blockHonorsOptionNav` вычислителя графа, тот же, что у сервера.
 * Скрытый правилом блок путь не диктует (как single_choice, WEB-1456).
 * Варианты — по обоим именам пары (`imageChoiceOptions` кабинета
 * приоритетнее `options`).
 */
export function singleImageChoicePick(
  blocks: readonly EditorBlock[],
  hidden: ReadonlySet<string>,
  values: Readonly<Record<string, string | undefined>>,
): { blockType: 'image_choice'; nextScreenId?: string } | undefined {
  const block = blocks.find(
    (b) =>
      b.type === 'image_choice' &&
      !hidden.has(b.id) &&
      !!values[b.id] &&
      blockHonorsOptionNav(b),
  );
  if (!block) return undefined;
  const ai = (block as unknown as { options?: ImageChoiceOption[] }).options;
  const options = block.imageChoiceOptions ?? ai ?? [];
  return {
    blockType: 'image_choice',
    nextScreenId: options.find((o) => o.id === values[block.id])?.nextScreenId,
  };
}

// Block types whose `values[block.id]` represents a user answer worth tracking.
// `email` is included but its value is redacted (PII).
const ANSWER_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'single_choice',
  'multiple_choice',
  'email',
  'text_field',
  'phone_input',
  'date_picker',
  'number_input',
  'select_dropdown',
  'rating_stars',
  // Атом 8 (аудит 2026-07-31): шкала-вопрос пишет values[id] как single_choice —
  // без записи здесь её ответы молча выпадали из SCREEN_ANSWER-аналитики.
  'likert_scale',
  // WEB-926 (5-6): троичная реакция (disagree|unsure|agree) и бинарная
  // карточка предпочтения (no|yes) — оба пишут values[id] кликом, значит
  // обязаны попадать в SCREEN_ANSWER. Тот же класс, что потеря likert выше.
  'statement_agreement',
  'preference_card',
  'slider_range',
  'toggle',
  'name_field',
  'checkbox',
  // WEB-1301 (F119/F378): все четыре пишут values[block.id] кликом/вводом и
  // доступны автору в палитре — тот же класс, что likert_scale/statement_
  // agreement/preference_card выше (отставший вручную реестр). Гвардирует
  // answerCollection.guard.web1301.test.ts.
  'multi_step_question',
  'image_choice',
  'weight_picker',
  'height_picker',
]);

/**
 * WEB-1011: тот же список нужен экрану загрузки — сводка ответов обязана
 * показывать ровно те записи, которые аналитика считает ответом. Отдельной
 * копии списка быть не должно (шрам WEB-953: копия фильтра расходится с
 * оригиналом молча), поэтому наружу отдаётся предикат, а не сам набор.
 */
export function isAnswerBlockType(type: string): boolean {
  return ANSWER_BLOCK_TYPES.has(type);
}

export interface ScreenAnswerPayload {
  blockId: string;
  blockType: string;
  value: string;
  /** WEB-1140: текст вопроса, который видел пользователь; пусто → поля нет. */
  label?: string;
}

export function collectScreenAnswers(
  blocks: EditorBlock[],
  values: Record<string, string>,
  hiddenBlockIds: Set<string> = new Set(),
  // Ревизия 45 (C6): решение по блоку согласия действует на ВСЁ прохождение, а
  // гард ставит один блок на воронку — поэтому решение приносит вызывающий
  // (плеер знает все экраны, сборщик — только свой,
  // `resolveFunnelSensitiveConsent`). Не принесли — судим по своему экрану,
  // как раньше: остальные вызывающие места поведения не меняют.
  consent: SensitiveConsentState = readSensitiveConsent(
    blocks,
    values,
    hiddenBlockIds,
  ),
): ScreenAnswerPayload[] {
  const out: ScreenAnswerPayload[] = [];
  // WEB-2158 п.5: блок явного согласия запирает сбор ответов — без
  // обязательной галочки чувствительный ответ не сохраняется и наружу не
  // уходит (правовой эффект даёт именно несохранение). Посетителя при этом
  // пускают дальше: гейт экрана (requiredGate) этот блок не держит.
  for (const block of blocks) {
    // Сам факт решения записывается ВСЕГДА и двумя раздельными записями —
    // и когда согласия нет: «отказ» это тоже решение, и его надо уметь
    // показать (WEB-2162 п.3).
    if (block.type === SENSITIVE_CONSENT_BLOCK_TYPE) {
      if (hiddenBlockIds.has(block.id)) continue;
      const collectLabel = questionLabel(block.content);
      out.push({
        blockId: block.id,
        blockType: SENSITIVE_CONSENT_BLOCK_TYPE,
        value: values[block.id] === 'true' ? 'true' : 'false',
        ...(collectLabel !== undefined && { label: collectLabel }),
      });
      if (sensitiveShareEnabled(block)) {
        const shareKey = sensitiveShareValueKey(block.id);
        const shareLabel = questionLabel(
          (block as { sensitiveShareContent?: unknown }).sensitiveShareContent,
        );
        out.push({
          blockId: shareKey,
          blockType: SENSITIVE_CONSENT_SHARE_BLOCK_TYPE,
          value: values[shareKey] === 'true' ? 'true' : 'false',
          ...(shareLabel !== undefined && { label: shareLabel }),
        });
      }
      continue;
    }
    if (consent.present && !consent.collectGranted) continue;
    // F1c (A4): hidden blocks are excluded from SCREEN_ANSWER analytics.
    // Their answer values remain in `values`/EvalContext so other rules can
    // still reference them — do NOT delete from values on HIDE.
    if (hiddenBlockIds.has(block.id)) continue;
    // WEB-925 (гэп 1): ответы модалок лоадера живут не в values[block.id], а под
    // собственными ключами — без этой ветки они выпали бы из аналитики так же,
    // как ответы шкалы до WEB-923. Сам блок остаётся не-ответным (маркер 'done'
    // ответом не считается).
    if (block.type === 'loader_processing') {
      loaderQuestionList(block).forEach((question, index) => {
        const key = loaderQuestionValueKey(block.id, index);
        const answer = values[key];
        if (answer == null || answer === '') return;
        const label = questionLabel(question?.question);
        out.push({
          blockId: key,
          blockType: LOADER_QUESTION_BLOCK_TYPE,
          value: String(answer).slice(0, 255),
          ...(label !== undefined && { label }),
        });
      });
      continue;
    }
    if (!ANSWER_BLOCK_TYPES.has(block.type)) continue;
    const raw = values[block.id];
    if (raw == null || raw === '') continue;
    let value = block.type === 'email' ? '<redacted>' : String(raw).slice(0, 255);
    // WEB-1301 (Осторожно #4): голое число нечитаемо — "70" не говорит, кг это
    // или фунты. Единица дописывается только тут, во внешнем представлении
    // ответа; values[block.id] остаётся чистым числом для logic-engine.
    if (block.type === 'weight_picker' || block.type === 'height_picker') {
      const unit = values[pickerUnitValueKey(block.id)] ?? defaultPickerUnit(block);
      value = `${value} ${unit}`.slice(0, 255);
    }
    const label = questionLabel(block.content);
    out.push({
      blockId: block.id,
      blockType: block.type,
      value,
      ...(label !== undefined && { label }),
    });
  }
  return out;
}

// ─── Правило «на экране есть чем продвинуться» (WEB-1704) ───────────────────
// Переехало сюда из `screenAdvance.ts` (WEB-3767, возврат 26.10, решение
// владельца): правило нужно и плееру, и холсту кабинета (кнопка «Опубликовать»
// всей воронки), и отдельным модулем сборщик выносил его в свой кусок первого
// экрана публичной воронки (+1 запрос, +209 сырых байт; сторожа WEB-1988 и
// WEB-2015). Рядом с `blockAutoAdvances`, на котором построено, оно лежит в
// общем куске. `screenAdvance.ts` — только пересылка этих пяти имён; второго
// определения там нет (сторож — answerCollection.screenAdvance.web3767.test.ts).
// Шапка правила — в `screenAdvance.ts`.

/** Настоящий блок «Кнопка Далее» — автор его видит, правит и может удалить. */
export function isButtonNextBlock(b: EditorBlock): boolean {
  return b.type === 'button_next';
}

/**
 * WEB-919: обложка с непустым ctaText — уже кликабельный механизм продвижения
 * (hero-обёртка в визарде, WEB-906). Страховочная кнопка рядом с ней дала бы
 * две кнопки одного смысла на welcome-экране.
 */
export function isLiveHeroCta(b: EditorBlock): boolean {
  if (b.type !== 'hero') return false;
  const ctaText = (b as { ctaText?: unknown }).ctaText;
  return typeof ctaText === 'string' && ctaText.trim().length > 0;
}

/**
 * Экран продвигается сам: single_choice / statement_agreement /
 * preference_card / loader_processing с включённым авто-переходом
 * (blockAutoAdvances — WEB-926/682). Неявную запасную кнопку в этом случае не
 * рисуем: она лишняя и нередактируемая.
 */
export function screenAutoAdvances(blocks: readonly EditorBlock[]): boolean {
  return blocks.some(blockAutoAdvances);
}

/**
 * Есть ли у посетителя способ уйти с этого экрана вперёд.
 * false → проигрыватель покажет системную запасную кнопку, а конструктор
 * обязан об этом предупредить.
 */
export function screenHasAdvanceMeans(blocks: readonly EditorBlock[]): boolean {
  return (
    blocks.some(isButtonNextBlock) ||
    screenAutoAdvances(blocks) ||
    blocks.some(isLiveHeroCta)
  );
}

/**
 * WEB-1794: единственный способ уйти с экрана — авто-переход (нет ни блока
 * «Кнопка Далее», ни обложки с ctaText).
 *
 * Такой экран — тот самый, где запасной кнопки нет по построению
 * (screenHasAdvanceMeans истинно → ctaNode гасится). Если попытка авто-перехода
 * упёрлась в незавершённый ввод (WEB-1643) и человек замер с фокусом в поле —
 * уходить некуда. Проигрыватель показывает на таком экране запасную кнопку
 * «Далее» (QuizScreenWizard, ветка rescue). Там, где настоящая кнопка или
 * hero-CTA уже есть, показывать нечего — человеку есть куда нажать.
 *
 * Правило живёт здесь, рядом с тремя своими слагаемыми: собранное из них по
 * месту в визарде, оно стало бы четвёртой копией, которая разъедется при
 * добавлении нового способа продвижения.
 */
export function screenAdvanceIsAutoOnly(
  blocks: readonly EditorBlock[],
): boolean {
  return (
    screenAutoAdvances(blocks) &&
    !blocks.some(isButtonNextBlock) &&
    !blocks.some(isLiveHeroCta)
  );
}

/**
 * ⚠ Здесь живёт только ПРАВИЛО. Список «какие экраны предупредить» с
 * человеческими именами собирает конструктор
 * (editor/confirmPublishEmptyScreens.findScreensWithoutAdvance) — рядом с
 * близнецом findEmptyScreens.
 *
 * Причина разделения не косметическая: этот каталог — рантайм плеера, и
 * статический гард WEB-967 запрещает здесь русские строковые литералы
 * (плеер двуязычный, все его тексты обязаны идти через getPublicQuizStrings).
 * Подпись «Без названия» — текст РУССКОГО кабинета, а не плеера, и её место
 * на стороне конструктора.
 */
